import {
    Button,
    CampaignsFactory,
    DataTable,
    DialogHandler,
    Dropdown,
    EmptyContent,
    EPartialInfoPool,
    EStorageType,
    ETaskType,
    fetchRoute,
    FullBar,
    GenericWrapperContext,
    getStorage,
    Header,
    Icon,
    ListCampaignModel,
    LoadingContainer,
    Search,
    SendersFactory,
    setStorage,
    SubNav,
    TDataTableColumnsData,
    TDataTableRowData,
    TGenericListReturn,
    TOrder,
    TPromisePoolPartialCampaignReports,
    TPromisePoolPartialList,
    TPromisePoolRef,
    TPromisePoolReturnData
} from "@cakemail-org/ui-components-v2"
import { TSubNavItem } from "@cakemail-org/ui-components-v2/dist/esm/components/subNav/types"
import { Stack } from "@mui/material"
import moment from "moment"
import { MouseEvent, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { showTaskDialog } from "../../../components/dialogs/export"
import { showRenameDialog } from "../../../components/dialogs/rename"
import { EPlanLimitsFeatures, showPlanLimitsDialog } from "../../../utils/limits_features"
import { campaignReportsPromisePool, listsPromisePool } from "../../../utils/promisesPools"
import { ERoutes } from "../../../utils/url"
import { TCampaignsListing } from "../types"
import { getListingColsData, getListingRows } from "../utils"

export function CampaignsListing({ initTab }: TCampaignsListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const navigate = useNavigate()

    const [openedSearch, setOpenedSearch] = useState(false)
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(25)
    const [order, setOrder] = useState<TOrder>("desc")
    const [orderBy, setOrderBy] = useState<keyof ListCampaignModel>("created_on")
    const [campaigns, setCampaigns] = useState<TGenericListReturn<ListCampaignModel>>()
    const [loading, setLoading] = useState(false)
    const [tabFilter, setTabFilter] = useState<string>(initTab || "all")

    const [partialLists, setPartialLists] = useState<TPromisePoolReturnData<TPromisePoolPartialList>>({})
    const partialListsPromiseRef = useRef<TPromisePoolRef>(undefined)

    const [partialReports, setPartialReports] = useState<TPromisePoolReturnData<TPromisePoolPartialCampaignReports>>({})
    const partialReportsPromiseRef = useRef<TPromisePoolRef>(undefined)

    const timeoutRef = useRef<NodeJS.Timeout>(undefined)

    useEffect(fetchCampaigns, [page, perPage, order, orderBy, search])

    useEffect(() => {

        fetchCampaigns()

        return () => {
            stopPromisePools()
            clearTimeoutRef()
        }
    }, [tabFilter])

    function clearTimeoutRef() {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = undefined
        }
    }

    function stopPromisePools() {
        try {
            partialListsPromiseRef.current?.stop()
            partialListsPromiseRef.current = undefined
        } catch (e) { /* pass */ }
    }

    function fetchCampaigns() {
        clearTimeoutRef()
        setLoading(true)
        const filters = []
        if (search) filters.push(`name==*${search}*`)

        if (tabFilter !== "all") {
            if (tabFilter === "delivering") filters.push("status==scheduled,delivering")
            else if (tabFilter === "delivered") filters.push("status==delivered,archived")
            else if (tabFilter === "incomplete") filters.push("status==incomplete")
        }

        CampaignsFactory.list({
            page: page,
            perPage: perPage,
            sort: order === "asc" ? orderBy : `-${orderBy}`,
            filter: filters.length === 0 ? undefined : filters.join(";"),
            withCount: true,
        }).then((data) => {
            let hasLive = false
            const partialReports = getStorage({ key: EPartialInfoPool.partialCampaignReports, type: EStorageType.session })
            data.data.forEach((c) => {
                if (partialReports) {
                    const liveDiff = moment().diff(
                        moment(moment.unix(c.scheduled_for)),
                        "hours"
                    )
                    if (liveDiff < 48) {
                        hasLive = true
                    }
                    if (
                        (tabFilter === "delivering" && c.status === "scheduled") ||
                        (tabFilter === "delivering" && c.status === "delivering") ||
                        (tabFilter === "delivered" && liveDiff < 48)
                    ) {
                        delete partialReports[c.id.toString()]
                    }
                }
            })

            if (partialReports) {
                setStorage({ key: EPartialInfoPool.partialCampaignReports, data: partialReports, type: EStorageType.session })
            }

            setTimeoutFetchCampaigns({ hasLive })

            listsPromisePool({ data: data.data, dataIdPath: "audience.list_id", setter: setPartialLists, poolRef: partialListsPromiseRef })
            campaignReportsPromisePool({ data: data.data, setter: setPartialReports, poolRef: partialReportsPromiseRef })
            setCampaigns(data)
        }).catch((err) => {

        }).finally(() => {
            setLoading(false)
        })
    }

    function setTimeoutFetchCampaigns({ hasLive }: { hasLive: boolean }) {
        if (tabFilter === "delivered" && hasLive) {
            timeoutRef.current = setTimeout(() => {
                fetchCampaigns()
            }, 60 * 1000)
        } else if (tabFilter === "delivering") {
            timeoutRef.current = setTimeout(() => {
                fetchCampaigns()
            }, 30 * 1000)
        }
    }

    function showErrorDialog(e: any) {
        if (e) {
            DialogHandler.show({
                type: "error",
                title: t("general-error-action")
            })
        }
    }

    function handleToggleSearch() {
        setOpenedSearch(prev => !prev)
    }

    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function handleSearchChange(cSearch: string) {
        setPage(1)
        setSearch(cSearch)
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as keyof ListCampaignModel)
    }

    function handlePaginationChange(cPage: number, cPerPage: number) {
        setPage(cPage)
    }

    function handleChangeTabFilter(event: MouseEvent<HTMLButtonElement>, item: TSubNavItem) {
        let route = ERoutes.campaigns
        if (item.name === "incomplete") route = ERoutes.campaignDrafts
        else if (item.name === "delivering") route = ERoutes.campaignOutbox
        else if (item.name === "delivered") route = ERoutes.campaignSent

        navigate(fetchRoute({ route: route }))

        setTabFilter(item.name)
    }

    function handleOpenCampaign(c: ListCampaignModel) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.campaignEdit, params: { campaignId: c.id } })
    }

    function handleViewReport(c: ListCampaignModel) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.campaignReport, params: { campaignId: c.id } })
    }

    function handleCreateCampaign() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.campaignsCreate })
    }

    async function handleExport() {
        if (showPlanLimitsDialog({
            name: EPlanLimitsFeatures.use_campaigns_reports_export,
            account: genericWrapper.account,
            user: genericWrapper.user,
            partnerBrand: genericWrapper.partnerBrand
        })) {
            return
        }

        showTaskDialog({
            taskType: ETaskType.campaigns_reports_export
        })
    }

    function handleViewExports() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.export })
    }


    function handleDuplicateCampaign(c: ListCampaignModel) {

        Promise.allSettled([
            CampaignsFactory.get({ id: c.id }),
            SendersFactory.list({ perPage: 200 })
        ]).then(([campaign, senders]) => {
            if (campaign.status === "rejected" || senders.status === "rejected") {
                throw new Error()
            }

            const campaignData = campaign.value.toJson()
            campaignData.name = campaign.value.name + ` (${t("general-copy")})`

            const foundSender = senders.value.data.find((sender) => {
                if (sender.email === campaign.value.sender?.email) {
                    return true
                }
                return false
            })

            if (!foundSender) {
                throw new Error("Sender not found")
            }


            campaignData.sender = { id: foundSender.id }

            return CampaignsFactory.create({ campaign: campaignData })
        }).then(() => {
            DialogHandler.show({
                type: "success",
                title: t("general-duplicate-success", { name: c.name })
            })
            fetchCampaigns()
        }).catch((e) => {
            showErrorDialog(e)
        })
    }

    function handleDeleteCampaign(c: ListCampaignModel) {
        showDeleteDialog({
            name: c.name,
            resourceKey: "general-form_one",
        }).then(({ event, btnRef }) => {
            btnRef.animate()
            return c.delete()
        }).then(() => {
            DialogHandler.show({
                isExpress: true,
                type: "success",
                title: t("general-resource-deleted", { resource: t("general-campaign_one") })
            })
            fetchCampaigns()
        }).catch((e) => {
            showErrorDialog(e)
        })
    }

    function handleCancelCampaign(c: ListCampaignModel) {
        c.cancel().then(() => {
            fetchCampaigns()
        }).catch((e) => {
            showErrorDialog(e)
        })
    }

    function handlePauseCampaign(c: ListCampaignModel) {
        c.suspend().then(() => {
            fetchCampaigns()
        }).catch((e) => {
            showErrorDialog(e)
        })
    }

    function handleResumeCampaign(c: ListCampaignModel) {
        c.resume().then(() => {
            fetchCampaigns()
        }).catch((e) => {
            showErrorDialog(e)
        })
    }

    function handleUnscheduleCampaign(c: ListCampaignModel) {
        c.unSchedule().then(() => {
            fetchCampaigns()
        }).catch((e) => {
            showErrorDialog(e)
        })
    }

    function handleRenameCampaign(c: ListCampaignModel) {
        showRenameDialog({ initName: c.name, resourceKey: "general-campaign_one" })
            .then(({ value }) => {
                return c.save({ name: value })
            }).then(() => {
                fetchCampaigns()
                DialogHandler.hide()
            }).catch((e) => {
                showErrorDialog(e)
            })
    }


    const colsData: TDataTableColumnsData[] = useMemo(getListingColsData({ tabFilter }), [tabFilter])

    const rows: TDataTableRowData[] = useMemo(() => campaigns?.data.map((c) => {
        const listName = partialLists?.[c.audience?.list_id || 0]?.name
        const partialReport: TPromisePoolPartialCampaignReports = partialReports?.[c.id] || {} as TPromisePoolPartialCampaignReports

        return getListingRows({
            c,
            partialReport,
            listName,
            tabFilter,
            genericWrapper,
            handleDuplicateCampaign,
            handleDeleteCampaign,
            handleUnscheduleCampaign,
            handlePauseCampaign,
            handleResumeCampaign,
            handleCancelCampaign,
            handleRenameCampaign,
            handleOpenCampaign,
            handleViewReport
        })
    }) || [], [campaigns?.data, tabFilter, order, orderBy, page, perPage])



    let emptyContentTitle = t("campaigns-listing-empty-title")
    let emptyContentDesc = t("campaigns-listing-empty-desc")
    let emptyContentIcon = "EmptyDraft44"

    if (tabFilter === "delivering") {
        emptyContentTitle = t("campaigns-listing-empty-title-outbox")
        emptyContentDesc = t("campaigns-listing-empty-desc-outbox")
        emptyContentIcon = "EmptyOutbox44"

    } else if (tabFilter === "delivered") {
        emptyContentTitle = t("campaigns-listing-empty-title-sent")
        emptyContentDesc = t("campaigns-listing-empty-desc-sent")
        emptyContentIcon = "EmptySent44"
    }


    return <Stack className="campaignsListingContainer" gap={8}>
        <FullBar
            className={loading ? "loading" : ""}
            left={<Header
                heading={t("general-campaign_other")}
                headingVariant="titleL"
                support={genericWrapper.account?.name}
                headingTooltip={t("campaign-heading-tooltip")}
            />}
            right={[
                <Button
                    key="create-form"
                    variant="contained"
                    size="medium"
                    color="secondary"
                    onClick={handleCreateCampaign}
                    iconOnly
                    disabled={loading}
                ><Icon name="Add18" /></Button>,
                <Button
                    variant="outlined"
                    size="medium"
                    color="body1"
                    onClick={handleToggleSearch}
                    iconOnly
                    disabled={loading}
                ><Icon name="Search18" /></Button>,
                <Dropdown
                    options={[
                        { text: t("campaigns-listing-export"), isAction: true, value: "export", onClick: handleExport },
                        { text: t("general-view-exports"), isAction: true, value: "my-exports", onClick: handleViewExports },
                    ]}
                    placeholder={t("general-export_other")}
                />
            ]}
        />

        {openedSearch &&
            <Search placeholder={t("campaigns-listing-search-placeholder")} initValue={search} className={`campaignsSearch ${loading ? "loading" : ""}`} onChange={handleSearchChange} onClose={handleSearchOpen} useClickAway />
        }

        <SubNav
            onClick={handleChangeTabFilter}
            initialSelected={tabFilter}
            items={[
                { text: t("general-all"), name: "all" },
                { text: t("general-draft"), name: "incomplete" },
                { text: t("general-outbox"), name: "delivering" },
                { text: t("general-sent"), name: "delivered" },
            ]}
        />

        <LoadingContainer loading={!campaigns}>
            {campaigns && campaigns.data.length === 0 && !search &&
                <EmptyContent
                    title={emptyContentTitle}
                    desc={emptyContentDesc}
                    button={<Button
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreateCampaign} iconOnly>
                        {t("general-create-campaign")}
                    </Button>}
                    iconName={emptyContentIcon}
                />
            }

            {campaigns && campaigns.data.length === 0 && search &&
                <EmptyContent
                    title={t("campaigns-listing-empty-search-title")}
                    desc={t("search-empty-desc")}
                    button={<Button
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreateCampaign} iconOnly>
                        {t("general-create-campaign")}
                    </Button>}
                />
            }

            {campaigns && campaigns.data.length > 0 &&
                <DataTable
                    initState={{
                        rowsPerPage: perPage,
                        order,
                        orderBy
                    }}
                    totalCount={campaigns?.pagination?.count}
                    className={`campaignsDataTable ${loading ? "loading" : ""}`}
                    onPaginationChange={handlePaginationChange}
                    onSortChange={handleSortChange}
                    rows={rows}
                    columnsData={colsData}
                    stickyLastCol
                />
            }

        </LoadingContainer>
    </Stack>
}